import { useMemo, useState } from "react";

import AccountMenu from "@/components/account/AccountMenu";
import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/ToastProvider";
import { useAuth } from "@/hooks/useAuth";
import type { AppProfile } from "@/lib/appProfile";
import { cn } from "@/lib/classNames";
import {
  useCurrentProfileQuery,
  useDeleteCurrentProfileMutation,
  useUpdateCurrentProfileMutation,
} from "@/lib/queries/profileQueries";

function getEditableDisplayName(profile: Pick<AppProfile, "display_name" | "email">): string {
  const displayName = profile.display_name?.trim();
  if (displayName) {
    return displayName;
  }

  const email = profile.email?.trim();
  if (email) {
    return email.split("@")[0] || email;
  }

  return "";
}

export default function ProfilePageClient({
  profile,
}: {
  profile: AppProfile;
}) {
  const toast = useToast();
  const { signOut } = useAuth();
  const { data: currentProfile = profile } = useCurrentProfileQuery(profile);
  const updateProfileMutation = useUpdateCurrentProfileMutation();
  const deleteProfileMutation = useDeleteCurrentProfileMutation();
  const [displayName, setDisplayName] = useState(() => getEditableDisplayName(profile));
  const [deletePanelOpen, setDeletePanelOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const trimmedName = displayName.trim();
  const saving = updateProfileMutation.isPending;
  const deleting = deleteProfileMutation.isPending;
  const emailLabel = currentProfile.email?.trim() || "No email on file";
  const canSave =
    trimmedName.length > 0 && trimmedName !== (currentProfile.display_name?.trim() || "");
  const canDelete = deleteConfirmation === "DELETE" && !deleting;
  const initialsLabel = useMemo(() => (trimmedName || emailLabel).slice(0, 1).toUpperCase(), [emailLabel, trimmedName]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!trimmedName || saving) {
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({ displayName: trimmedName });
      toast.success("Profile saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update profile.");
    }
  }

  async function handleDeleteAccount(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canDelete) {
      return;
    }

    try {
      await deleteProfileMutation.mutateAsync({ confirmation: deleteConfirmation });
      toast.success("Account deleted.");
      await signOut();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete account.");
    }
  }

  return (
    <div className={cn("min-h-screen", MONOLITH_BOARD_STYLE.shellClassName)}>
      <div className="mx-auto flex min-h-screen w-full max-w-[1180px] flex-col px-4 py-6 sm:px-6 lg:px-10">
        <header
          className={cn(
            "flex flex-col gap-4 py-4 pl-6 pr-4 sm:gap-6 sm:py-5 sm:pl-8 sm:pr-5",
            MONOLITH_BOARD_STYLE.dashboardHeaderPanelClassName,
          )}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 max-w-3xl">
              <p className="text-[10px] uppercase tracking-[0.34em] text-neutral-600 sm:text-[11px]">Account</p>
              <h1 className="mt-2 text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">Profile</h1>
            </div>
            <div className="flex items-start gap-3 self-start">
              <Button href="/" variant="cancel" chrome="frame" className="min-h-14 px-6 py-4">
                Back to map
              </Button>
              <AccountMenu profile={currentProfile} />
            </div>
          </div>
        </header>

        <main className="mt-6 flex-1">
          <section className={cn("grid gap-6 p-6 sm:p-8 lg:grid-cols-[0.8fr_1.2fr]", MONOLITH_BOARD_STYLE.panelClassName)}>
            <aside className="border-b-2 border-neutral-950/12 pb-6 lg:border-b-0 lg:border-r-2 lg:pb-0 lg:pr-6">
              <p className="text-[10px] uppercase tracking-[0.34em] text-neutral-600">Identity</p>
              <div className="mt-5 flex items-center gap-4">
                <div className="flex h-20 w-20 items-center justify-center rounded-full border-[3px] border-neutral-950 bg-[#23443a] text-3xl font-black uppercase text-[#fff7ec]">
                  {initialsLabel}
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-black uppercase tracking-[-0.03em] text-neutral-950">
                    {trimmedName || "Unnamed player"}
                  </p>
                  <p className="mt-1 break-all text-xs uppercase tracking-[0.18em] text-neutral-600">{emailLabel}</p>
                </div>
              </div>
            </aside>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label
                  htmlFor="display-name"
                  className="block text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600"
                >
                  Username
                </label>
                <input
                  id="display-name"
                  name="displayName"
                  type="text"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  className="mt-3 w-full border-[3px] border-neutral-950 bg-[#fff7ec] px-4 py-4 text-base font-semibold text-neutral-950 outline-none transition-shadow focus-visible:shadow-[6px_6px_0_rgba(62,102,77,0.28)]"
                  autoComplete="name"
                  spellCheck={false}
                />
              </div>

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600">Email</p>
                <div className="mt-3 border-[3px] border-neutral-950 bg-[#fff2d9] px-4 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-950">
                  {emailLabel}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 border-t-2 border-neutral-950/12 pt-6">
                <Button
                  type="submit"
                  variant="primary"
                  chrome="frame"
                  disabled={!canSave || saving}
                  className="min-h-14 px-8 py-4"
                >
                  {saving ? "Saving..." : "Save profile"}
                </Button>
                <Button
                  type="button"
                  variant="cancel"
                  chrome="frame"
                  disabled={saving}
                  className="min-h-14 px-8 py-4"
                  onClick={() => setDisplayName(getEditableDisplayName(currentProfile))}
                >
                  Reset
                </Button>
              </div>
            </form>
          </section>

          <section className={cn("mt-6 p-6 sm:p-8", MONOLITH_BOARD_STYLE.panelClassName)}>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-2xl">
                <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-[#9c4c32]">
                  Danger zone
                </p>
                <h2 className="mt-2 text-2xl font-black tracking-[-0.04em] text-neutral-950">
                  Delete account
                </h2>
                <p className="mt-3 text-sm leading-6 text-neutral-700">
                  Permanently deletes your account, campaigns, notes, memberships, saved catalog
                  assets, and feedback content. This cannot be undone.
                </p>
              </div>

              {!deletePanelOpen ? (
                <Button
                  type="button"
                  variant="destructive"
                  chrome="frame"
                  disabled={saving || deleting}
                  className="min-h-14 px-8 py-4"
                  onClick={() => setDeletePanelOpen(true)}
                >
                  Delete account
                </Button>
              ) : null}
            </div>

            {deletePanelOpen ? (
              <form className="mt-6 border-t-2 border-neutral-950/12 pt-6" onSubmit={handleDeleteAccount}>
                <label
                  htmlFor="delete-account-confirmation"
                  className="block text-[10px] font-semibold uppercase tracking-[0.32em] text-neutral-600"
                >
                  Type DELETE to confirm
                </label>
                <input
                  id="delete-account-confirmation"
                  type="text"
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  className="mt-3 w-full max-w-md border-[3px] border-neutral-950 bg-[#fff7ec] px-4 py-4 text-base font-semibold text-neutral-950 outline-none transition-shadow focus-visible:shadow-[6px_6px_0_rgba(156,76,50,0.24)]"
                  autoComplete="off"
                  spellCheck={false}
                />

                <div className="mt-4 flex flex-wrap gap-3">
                  <Button
                    type="submit"
                    variant="destructive"
                    chrome="frame"
                    disabled={!canDelete}
                    className="min-h-14 px-8 py-4"
                  >
                    {deleting ? "Deleting..." : "Permanently delete account"}
                  </Button>
                  <Button
                    type="button"
                    variant="cancel"
                    chrome="frame"
                    disabled={deleting}
                    className="min-h-14 px-8 py-4"
                    onClick={() => {
                      setDeletePanelOpen(false);
                      setDeleteConfirmation("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : null}
          </section>
        </main>
      </div>
    </div>
  );
}
